import React from 'react';
import { Helmet } from 'react-helmet';

const PrivacyPolicyPage = () => (
  <>
    <Helmet>
      <title>Privacy Policy | Kabett</title>
      <meta name="description" content="Privacy Policy for Kabett products including Android apps, Chrome extensions, and web services." />
    </Helmet>
    <section className="prose max-w-none font-sans">
      <h1 className="text-4xl font-serif font-bold mb-8 text-gray-800">Privacy Policy</h1>
      <p className="text-sm text-gray-500 mb-8">Last updated: 29 December 2024</p>

      <p className="mb-8">At Kabett, we are committed to protecting your privacy and ensuring you have a positive experience on our websites and while using our products. This policy outlines our data handling practices for our various products.</p>

      <h2 className="text-2xl font-serif font-bold mt-12 mb-4 text-gray-800">1. Our Products</h2>
      <p className="mb-6">This privacy policy covers the following Kabett products:</p>

      <h3 className="text-xl font-serif font-bold mt-8 mb-4 text-gray-700">Web Services</h3>
      <ul className="list-disc pl-6 mb-6">
        <li className="mb-2"><strong>MummaCare (mummacare.com)</strong> - Maternal care platform</li>
        <li className="mb-2"><strong>Kabett.com</strong> - Company website</li>
      </ul>

      <h3 className="text-xl font-serif font-bold mt-8 mb-4 text-gray-700">Android Apps</h3>
      <ul className="list-disc pl-6 mb-6">
        <li className="mb-2"><strong>Kannada Janapada</strong> - Kannada folk songs and videos</li>
        <li className="mb-2"><strong>Hindu Gods Quiz</strong> - Educational quiz about Hindu mythology</li>
        <li className="mb-2"><strong>Breath Break Pranayama</strong> - Breathing exercises and meditation</li>
        <li className="mb-2"><strong>Hanuman Chalisa Hindi</strong> - Devotional app with Hanuman Chalisa in Hindi</li>
        <li className="mb-2"><strong>Hanuman Chalisa Kannada</strong> - Devotional app with Hanuman Chalisa in Kannada</li>
        <li className="mb-2"><strong>Resume Maker</strong> - Resume/CV creation tool</li>
        <li className="mb-2"><strong>Kannada Akshara</strong> - Kannada alphabet learning app</li>
      </ul>

      <h3 className="text-xl font-serif font-bold mt-8 mb-4 text-gray-700">Browser Extensions</h3>
      <ul className="list-disc pl-6 mb-8">
        <li className="mb-2"><strong>Pranayama Chrome Extension</strong> - Breathing exercises for your browser</li>
      </ul>

      <h2 className="text-2xl font-serif font-bold mt-12 mb-4 text-gray-800">2. Information We Collect</h2>
      <p className="mb-6">The type and amount of information we collect depends on how you use our products:</p>

      <h3 className="text-xl font-serif font-bold mt-8 mb-4 text-gray-700">Android Apps (No Data Collection)</h3>
      <p className="mb-4">Our Android apps are designed with privacy in mind. The following apps do <strong>not collect any personal data, usage information, or analytics</strong>:</p>
      <ul className="list-disc pl-6 mb-6">
        <li className="mb-2">Kannada Janapada</li>
        <li className="mb-2">Hindu Gods Quiz</li>
        <li className="mb-2">Breath Break Pranayama</li>
        <li className="mb-2">Hanuman Chalisa Hindi</li>
        <li className="mb-2">Hanuman Chalisa Kannada</li>
        <li className="mb-2">Resume Maker</li>
        <li className="mb-2">Kannada Akshara</li>
      </ul>
      <p className="mb-6">These apps:</p>
      <ul className="list-disc pl-6 mb-8">
        <li className="mb-2">Do not contain advertisements</li>
        <li className="mb-2">Do not use tracking or analytics services</li>
        <li className="mb-2">Do not collect or transmit any personal information</li>
        <li className="mb-2">Do not require user accounts or registration</li>
        <li className="mb-2">Store all data locally on your device only</li>
      </ul>

      <h3 className="text-xl font-serif font-bold mt-8 mb-4 text-gray-700">Pranayama Chrome Extension (No Data Collection)</h3>
      <p className="mb-8">The Pranayama Chrome Extension does not collect any personal data or usage information. All settings are stored locally in your browser.</p>

      <h3 className="text-xl font-serif font-bold mt-8 mb-4 text-gray-700">Web Services</h3>
      <ul className="list-disc pl-6 mb-8">
        <li className="mb-2"><strong>MummaCare:</strong> We use Google Analytics to collect standard internet log information and details of visitor behavior patterns. This information is processed in a way that does not identify anyone.</li>
        <li className="mb-2"><strong>Kabett.com:</strong> We use Google Analytics to understand how visitors interact with our website. This helps us improve our services.</li>
      </ul>

      <h2 className="text-2xl font-serif font-bold mt-12 mb-4 text-gray-800">3. How We Use Your Information</h2>
      <p className="mb-6">For our web services that use analytics, we use the collected information to:</p>
      <ul className="list-disc pl-6 mb-8">
        <li className="mb-2">Analyze website usage to improve user experience</li>
        <li className="mb-2">Understand which content is most helpful to our visitors</li>
        <li className="mb-2">Conduct research and analysis to maintain, protect, develop, and improve our products</li>
      </ul>
      <p className="mb-8">For our Android apps and Chrome extension, as no data is collected, we do not use any personal information.</p>

      <h2 className="text-2xl font-serif font-bold mt-12 mb-4 text-gray-800">4. Data Sharing and Disclosure</h2>
      <p className="mb-8">We do not sell your personal information to third parties. For our web services, we may share non-personally identifiable, aggregated information for analytical purposes.</p>

      <h2 className="text-2xl font-serif font-bold mt-12 mb-4 text-gray-800">5. Data Security</h2>
      <p className="mb-8">We implement appropriate security measures to maintain the safety of any information we collect. For our Android apps, all data remains on your device and is not transmitted to any servers.</p>

      <h2 className="text-2xl font-serif font-bold mt-12 mb-4 text-gray-800">6. Cookies and Tracking Technologies</h2>
      <p className="mb-4">Our usage of cookies and tracking varies by product:</p>
      <ul className="list-disc pl-6 mb-8">
        <li className="mb-2"><strong>Android Apps:</strong> No cookies or tracking technologies are used.</li>
        <li className="mb-2"><strong>Chrome Extension:</strong> No cookies or tracking technologies are used.</li>
        <li className="mb-2"><strong>Web Services:</strong> Our websites use cookies for Google Analytics. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.</li>
      </ul>

      <h2 className="text-2xl font-serif font-bold mt-12 mb-4 text-gray-800">7. Children's Privacy</h2>
      <p className="mb-4">Some of our apps, such as Hindu Gods Quiz and Kannada Akshara, are designed to be educational and family-friendly. These apps:</p>
      <ul className="list-disc pl-6 mb-8">
        <li className="mb-2">Do not collect any personal information from users of any age</li>
        <li className="mb-2">Do not contain advertisements</li>
        <li className="mb-2">Do not have social features or external links</li>
        <li className="mb-2">Are safe for children to use under parental supervision</li>
      </ul>

      <h2 className="text-2xl font-serif font-bold mt-12 mb-4 text-gray-800">8. Your Rights</h2>
      <p className="mb-4">You have the right to:</p>
      <ul className="list-disc pl-6 mb-8">
        <li className="mb-2">Access any personal information we hold about you</li>
        <li className="mb-2">Request deletion of your data</li>
        <li className="mb-2">Opt out of analytics tracking on our websites by using browser settings or privacy extensions</li>
      </ul>

      <h2 className="text-2xl font-serif font-bold mt-12 mb-4 text-gray-800">9. Changes to This Privacy Policy</h2>
      <p className="mb-8">We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.</p>

      <h2 className="text-2xl font-serif font-bold mt-12 mb-4 text-gray-800">10. Contact Us</h2>
      <p className="mb-4">If you have any questions about this Privacy Policy or our data practices, please contact us at:</p>
      <p className="mb-8"><strong>Email:</strong> hi@kabett.com</p>
    </section>
  </>
);

export default PrivacyPolicyPage;
